import { useRequest } from 'ahooks';
import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import {
  Carousel,
  Modal,
  Image,
  Spin,
  Row,
  Col,
  Card,
  Space,
  Button,
  message,
} from 'antd';
import {
  companyMediaController,
  CompanyMediaTypeEnum,
  Long,
  oSSController,
} from '../../api';
import { useCompany } from '../../hooks/company';
import VideoPreviewWithThumbnail from '../../components/VideoPreviewWithThumbnail';
import { DeleteOutline } from 'antd-mobile-icons';

const refreshContext = createContext<{
  refresh: () => void;
}>({ refresh() {} });

const VideoPreview = ({ ossKey }: { ossKey: string }) => {
  const { id: companyId } = useCompany();
  const { data: imageUrl } = useRequest(
    () => {
      return oSSController.getOSSUrl(
        companyId,
        ossKey,
        undefined,
        'video/snapshot,t_17000,f_jpg,w_0,h_0',
      );
    },
    {
      refreshDeps: [companyId, ossKey],
    },
  );
  return <VideoPreviewWithThumbnail ossKey={ossKey} thumbnailUrl={imageUrl} />;
};

const ImagePreview = ({ ossKey }: { ossKey: string }) => {
  const { id: companyId } = useCompany();

  const { data: url } = useRequest(
    () => {
      return Promise.all([
        oSSController.getOSSUrl(companyId, ossKey),
        oSSController.getOSSUrl(
          companyId,
          ossKey,
          undefined,
          'image/resize,m_fill,w_400,h_400',
        ),
      ]);
    },
    {
      refreshDeps: [companyId, ossKey],
    },
  );

  return (
    <Image
      src={url?.[1]}
      preview={{
        src: url?.[0],
      }}
      // placeholder={<Image preview={false} src={url?.[1]} />}
    />
  );
};

const DeleteMedia = ({ id }: { id: Long }) => {
  const { id: companyId } = useCompany();
  const { refresh } = useContext(refreshContext);
  const { run, loading } = useRequest(
    () => {
      return companyMediaController.deleteMedia(companyId, id);
    },
    {
      manual: true,
      onError: (err) => {
        message.error(err.message);
      },
      onSuccess: () => {
        message.success('删除成功');
        refresh();
      },
    },
  );

  return (
    <Button
      icon={<DeleteOutline />}
      onClick={() => run()}
      loading={loading}
      size="small"
      danger
    />
  );
};

const DeleteWrapper = ({ id, children }: PropsWithChildren<{ id: Long }>) => {
  const { hasPermission } = useCompany();

  const canEdit = useMemo(() => {
    return hasPermission('page:media:editor');
  }, [hasPermission]);

  if (!canEdit) {
    return <>{children}</>;
  }

  return (
    <>
      <Card extra={[<DeleteMedia id={id} />]}>{children}</Card>
    </>
  );
};

export default ({
  category,
  companyId,
}: {
  category: string;
  companyId: Long;
}) => {
  const { data, loading, refresh } = useRequest(
    () => {
      return companyMediaController
        .listByCategory(companyId, category)
        .then((result) => {
          return {
            video: result.filter(
              ({ type }) => type === CompanyMediaTypeEnum.VIDEO,
            ),
            image: result.filter(
              ({ type }) => type === CompanyMediaTypeEnum.PHOTO,
            ),
          };
        });
    },
    {
      refreshDeps: [open, companyId, category],
    },
  );

  return (
    <refreshContext.Provider value={{ refresh: refresh }}>
      <Spin spinning={loading}>
        <Card title={`${category} 照片记忆`}>
          <Row gutter={10}>
            {data?.image.map((item) => {
              return (
                <Col key={item.id} span={6} style={{ paddingBottom: 10 }}>
                  <DeleteWrapper id={item.id}>
                    <ImagePreview ossKey={item.ossKey} />
                  </DeleteWrapper>
                </Col>
              );
            })}
          </Row>
        </Card>
        {(data?.video.length || 0) > 0 && (
          <Card title={`${category} 视频记忆`} style={{ marginTop: 20 }}>
            <Row gutter={10}>
              {data?.video.map((item) => {
                return (
                  <Col key={item.id} span={6} style={{ paddingBottom: 10 }}>
                    <DeleteWrapper id={item.id}>
                      <VideoPreview ossKey={item.ossKey} />
                    </DeleteWrapper>
                  </Col>
                );
              })}
            </Row>
          </Card>
        )}
      </Spin>
    </refreshContext.Provider>
  );
};
